<script lang="ts">
    import { Color } from "$lib/utility/color";
    import { createEventDispatcher } from "svelte";

    const dispatch = createEventDispatcher();

    export let color = Color.fromHex("#ff0000");
    $: updateColor(color);

    let hue = 0;
    let saturation = 100;
    let lightness = 50;
    let alpha = 255;
    let hex = "ff0000";

    let inputHex: HTMLInputElement;

    function beginPickHue(event: PointerEvent) {
        const target = event.target as HTMLElement;
        const rect = target.getBoundingClientRect();

        const center = { x: rect.width / 2, y: rect.height / 2 };

        pickHue(event);

        target.setPointerCapture(event.pointerId);
        target.addEventListener("pointermove", pickHue);
        target.addEventListener("pointerup", endPickHue);

        function pickHue(event: PointerEvent) {
            const angle = Math.atan2(event.clientY - rect.top - center.y, event.clientX - rect.left - center.x);
            hue = Math.round((angle * 180 / Math.PI + 90 + 360) % 360);

            updateColorFromHSL();
        }

        function endPickHue(event: PointerEvent) {
            target.releasePointerCapture(event.pointerId);
            target.removeEventListener("pointermove", pickHue);
            target.removeEventListener("pointerup", endPickHue);
        }
    }

    function validateHSL() {
        if (isNaN(hue) || hue < 0 || hue > 360) {
            return false;
        }
        if (isNaN(saturation) || saturation < 0 || saturation > 100) {
            return false;
        }
        if (isNaN(lightness) || lightness < 0 || lightness > 100) {
            return false;
        }
        return true;
    }

    let updating = false;

    function update(fn: () => void) {
        if (updating) return;
        updating = true;
        try {
            fn();
        } finally {
            setTimeout(() => updating = false);
        }
    }

    function updateColorFromHSL() {
        update(() => {
            if (!validateHSL()) {
                return;
            }
            color = Color.fromHSLA(hue, saturation, lightness, alpha);
            hex = Color.asHex(color).substring(1);
            inputHex.setCustomValidity("");
            colorChanged();
        });
    }

    function updateColorFromHex(e: Event) {
        update(() => {
            let input = e.target as HTMLInputElement;
            try {
                color = Color.fromHex(hex);
                const hsla = Color.asHSLA(color);
                hue = Math.round(hsla.h);
                saturation = Math.round(hsla.s);
                lightness = Math.round(hsla.l);
                alpha = Math.round(hsla.a);
                input.setCustomValidity("");
                colorChanged();
            } catch {
                input.setCustomValidity("Invalid HEX color");
            }
        });
    }

    function updateColor(c: any) {
        update(() => {
            const hsla = Color.asHSLA(color);
            hue = Math.round(hsla.h);
            saturation = Math.round(hsla.s);
            lightness = Math.round(hsla.l);
            alpha = Math.round(hsla.a);
            hex = Color.asHex(color).substring(1);
        });
    }

    function colorChanged() {
        dispatch("change", color);
    }
</script>

<!----------------------------------------------------------------------------->

<div class="color-picker-container">
    <div class="color-picker"
         style="--hue: {hue}deg; --saturation: {saturation}; --lightness: {lightness}; --hex: #{hex}">

        <div class="hue" on:pointerdown={beginPickHue}>
            <div class="color" />
        </div>

        <label>
            <span>Saturation</span>
            <input class="range-slider saturation" type="range" min="0" max="100" bind:value={saturation}
                   on:input={updateColorFromHSL} />
        </label>
        <label>
            <span>Lightness</span>
            <input class="range-slider lightness" type="range" min="0" max="100" bind:value={lightness}
                   on:input={updateColorFromHSL} />
        </label>
        <label>
            <span>Opacity</span>
            <input class="range-slider opacity" type="range" min="0" max="255" bind:value={alpha}
                   on:input={updateColorFromHSL} />
        </label>

        <label class="hex">
            <span>HEX</span>
            #<input type="text" maxlength="8" bind:value={hex}
                    on:keydown|stopPropagation
                    on:input={updateColorFromHex}
                    bind:this={inputHex} />
        </label>

        <label class="hsl">
            <span>HSL</span>
            <input type="number" bind:value={hue}
                   step="any"
                   min="0"
                   max="360"
                   on:input={updateColorFromHSL}
                   on:keydown|stopPropagation
            />

            <input type="number" bind:value={saturation}
                   step="any"
                   min="0"
                   max="100"
                   on:input={updateColorFromHSL}
                   on:keydown|stopPropagation
            />

            <input type="number" bind:value={lightness}
                   step="any"
                   min="0"
                   max="100"
                   on:input={updateColorFromHSL}
                   on:keydown|stopPropagation
            />
        </label>
    </div>
</div>


<!----------------------------------------------------------------------------->

<style>
    .color-picker-container {
        container: color-picker / inline-size;
        overflow: scroll;
    }

    .color-picker {
        display: grid;
        gap: 0.5em 1em;
        padding: 0.5em;

        grid-template:
            "hue" max-content
            "saturation" max-content
            "lightness" max-content
            "opacity" max-content
            "hex" max-content
            "hsl" max-content
            "other" 1fr
            / 1fr;

        @container color-picker (min-width: 20em) {
            grid-template:
                "hue saturation" max-content
                "hue lightness" max-content
                "hue opacity" max-content
                "hex hex" max-content
                "hsl hsl" max-content
                "other other" 1fr
                / max-content 1fr;

            & .hue {
                --radius: 4em;
            }
        }

        @container color-picker (min-width: 24em) {
            grid-template:
                "hue saturation" max-content
                "hue lightness" max-content
                "hue opacity" max-content
                "hue hex" max-content
                "hue hsl" max-content
                "other other" 1fr
                / max-content 1fr;

            & .hue {
                --radius: 5em;
            }
        }
    }

    .hue {
        grid-area: hue;

        --shadow: 0 0 4px #000a;
        --radius: 5em;
        --width: 1em;

        position: relative;
        width: calc(var(--radius) * 2);
        height: calc(var(--radius) * 2);
        background: conic-gradient(
                in hsl longer hue,
                hsl(0deg var(--saturation) var(--lightness)),
                hsl(360deg var(--saturation) var(--lightness))
        );
        border-radius: 50%;
        box-shadow: var(--shadow);

        &::before {
            content: "";
            position: absolute;
            inset: var(--width);
            background: var(--g100);
            border-radius: 50%;
            box-shadow: inset var(--shadow);
        }

        &::after {
            content: "";
            position: absolute;
            background: white;
            width: 3px;
            height: calc(var(--width) + 0.25em);
            transform: translate(calc(var(--radius) - 50%), calc(var(--radius) - 50%)) rotate(calc(var(--hue) + 180deg)) translateY(calc(var(--radius) - var(--width) / 2));
            outline: 1px solid #000a;
        }

        & .color {
            --width: 2em;
            position: absolute;
            inset: var(--width);
            border-radius: 50%;
            background: hsl(var(--hue) var(--saturation) var(--lightness));
            width: calc(100% - var(--width) * 2);
            height: calc(100% - var(--width) * 2);
            box-shadow: var(--shadow);
        }
    }

    label {
        line-height: 1;
        display: flex;
        align-items: center;

        & > span {
            color: var(--t400);
            font-size: 0.9rem;
            font-weight: bold;
        }
    }

    label:has(input[type="range"]) {
        flex-direction: column;
        align-items: flex-start;

        & > span {
            color: var(--t400);
            font-size: 0.8rem;
            margin-bottom: 0.25em;
        }
    }

    .range-slider {
        background: transparent;
        --height: 1em;
        width: 100%;
        padding: 1px 0px;
        margin: 0;
        box-sizing: border-box;

        &::-moz-range-track {
            background-image: var(--background);
            background-size: var(--background-size);
            background-position: var(--background-position);
            height: var(--height);
            outline: 1px solid var(--g400);
        }

        &::-webkit-slider-runnable-track {
            -webkit-appearance: none;
            appearance: none;
            background-image: var(--background);
            background-size: var(--background-size);
            background-position: var(--background-position);
            height: var(--height);
            outline: 1px solid var(--g400);
        }

        &::-moz-range-thumb {
            all: unset;
            background: white;
            width: 3px;
            height: calc(var(--height));
            outline: 1px solid #000a;
        }

        &::-webkit-slider-thumb {
            position: relative;
            -webkit-appearance: none;
            appearance: none;
            background: white;
            width: 3px;
            height: calc(var(--height));
            outline: 1px solid #000a;
            z-index: 100;
        }
    }

    .saturation {
        grid-area: saturation;
        --background: linear-gradient(
                to right,
                hsl(var(--hue) 0% var(--lightness)),
                hsl(var(--hue) 100% var(--lightness))
        );
    }

    .lightness {
        grid-area: lightness;
        --background: linear-gradient(
                to right,
                hsl(var(--hue) var(--saturation) 0%),
                hsl(var(--hue) var(--saturation) 50%),
                hsl(var(--hue) var(--saturation) 100%)
        );
    }

    .opacity {
        grid-area: opacity;

        --size: 1em;
        --size2: 0.5em;
        --size2n: -0.5em;
        --background: linear-gradient(
                to right,
                hsl(var(--hue) var(--saturation) var(--lightness) / 0%),
                hsl(var(--hue) var(--saturation) var(--lightness) / 100%)
        ),
        linear-gradient(45deg, #808080 25%, transparent 25%),
        linear-gradient(-45deg, #808080 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #808080 75%),
        linear-gradient(-45deg, transparent 75%, #808080 75%);
        --background-size: 100% 100%,
        var(--size) var(--size),
        var(--size) var(--size),
        var(--size) var(--size),
        var(--size) var(--size);
        --background-position: 0 0,
        0 0,
        0 var(--size2),
        var(--size2) var(--size2n),
        var(--size2n) 0;

    }

    .hex {
        display: grid;
        grid-area: hex;
        grid-template-columns: 1fr max-content max-content;
        gap: 0.25em;

        & input {
            font-family: var(--font-monospace);
            --width: 8ch;
        }
    }

    .hsl {
        display: grid;
        grid-area: hsl;
        grid-template-columns: 1fr repeat(3, max-content);
        gap: 0.25em;

        & input {
            --width: 3ch;
        }
    }

    input[type="number"]::-webkit-outer-spin-button,
    input[type="number"]::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        appearance: textfield;
    }

    input[type="number"], input[type="text"] {
        width: calc(var(--width) + 1ch + 4px);
    }
</style>
